import type {
  ConsolidadoPorDre,
  FiltrosRelatorioSme,
  IndicadoresSme,
  LinhaRelatorioSme,
  PontoSerieSme,
} from './tipos';

/**
 * As contas do Relatórios SME, fora do componente para o teste chegar nelas
 * sem montar a tela. Dinheiro sempre em centavos inteiros (`nucleo/dinheiro`),
 * nunca somado como ponto flutuante.
 */

export function aplicarFiltros(
  linhas: readonly LinhaRelatorioSme[],
  filtros: FiltrosRelatorioSme,
): LinhaRelatorioSme[] {
  return linhas.filter((linha) => {
    if (filtros.tipo !== 'TODOS' && linha.tipo !== filtros.tipo) return false;
    if (filtros.competencia !== 'TODOS' && linha.competencia !== filtros.competencia) return false;
    if (filtros.dreIds.length > 0 && !filtros.dreIds.includes(linha.dreId)) return false;
    return true;
  });
}

/** Média de prazo ponderada pelo número de medições — a média simples daria peso igual a DRE com 7 e com 30 medições. */
function prazoPonderado(linhas: readonly LinhaRelatorioSme[]): number | null {
  const medicoes = linhas.reduce((soma, l) => soma + l.medicoes, 0);
  if (medicoes === 0) return null;
  const dias = linhas.reduce((soma, l) => soma + l.prazoMedioDias * l.medicoes, 0);
  return dias / medicoes;
}

function razao(parte: number, todo: number): number | null {
  return todo === 0 ? null : parte / todo;
}

export function consolidarPorDre(linhas: readonly LinhaRelatorioSme[]): ConsolidadoPorDre[] {
  const porDre = new Map<string, { primeira: LinhaRelatorioSme; doDre: LinhaRelatorioSme[] }>();
  for (const linha of linhas) {
    const atual = porDre.get(linha.dreId);
    if (atual) atual.doDre.push(linha);
    else porDre.set(linha.dreId, { primeira: linha, doDre: [linha] });
  }

  const consolidado = Array.from(porDre.values()).map(({ primeira, doDre }) => {
    const somar = (campo: keyof LinhaRelatorioSme): number =>
      doDre.reduce((soma, l) => soma + (l[campo] as number), 0);

    // Credenciado não se soma entre competências: o mesmo aparece todo mês.
    // Por tipo, o maior valor; entre tipos, a soma — PJ e PF são conjuntos
    // distintos de credenciados da mesma DRE.
    const porTipo = new Map<string, number>();
    for (const l of doDre) {
      porTipo.set(l.tipo, Math.max(porTipo.get(l.tipo) ?? 0, l.credenciados));
    }
    const credenciados = Array.from(porTipo.values()).reduce((soma, n) => soma + n, 0);

    const apontamentos = somar('apontamentos');
    const devolucoes = somar('devolucoes');

    return {
      id: primeira.dreId,
      dreId: primeira.dreId,
      dreCodigo: primeira.dreCodigo,
      dreNome: primeira.dreNome,
      credenciados,
      apontamentos,
      devolucoes,
      percentualDevolucao: razao(devolucoes, apontamentos),
      prazoMedioDias: prazoPonderado(doDre),
      medicoes: somar('medicoes'),
      pagas: somar('pagas'),
      alunos: somar('alunos'),
      valorCentavos: somar('valorCentavos'),
      valorPagoCentavos: somar('valorPagoCentavos'),
    };
  });

  return consolidado.sort((a, b) => b.valorCentavos - a.valorCentavos);
}

export function calcularIndicadores(linhas: readonly LinhaRelatorioSme[]): IndicadoresSme {
  const somar = (campo: keyof LinhaRelatorioSme): number =>
    linhas.reduce((soma, l) => soma + (l[campo] as number), 0);

  const apontamentos = somar('apontamentos');
  const devolucoes = somar('devolucoes');

  return {
    valorCentavos: somar('valorCentavos'),
    valorPagoCentavos: somar('valorPagoCentavos'),
    medicoes: somar('medicoes'),
    alunos: somar('alunos'),
    devolucoes,
    apontamentos,
    percentualDevolucao: razao(devolucoes, apontamentos),
    prazoMedioDias: prazoPonderado(linhas),
  };
}

export function montarSerieTemporal(linhas: readonly LinhaRelatorioSme[]): PontoSerieSme[] {
  const porCompetencia = new Map<string, PontoSerieSme>();
  for (const linha of linhas) {
    const atual = porCompetencia.get(linha.competencia);
    if (atual) {
      atual.valorCentavos += linha.valorCentavos;
      atual.devolucoes += linha.devolucoes;
    } else {
      porCompetencia.set(linha.competencia, {
        competencia: linha.competencia,
        valorCentavos: linha.valorCentavos,
        devolucoes: linha.devolucoes,
      });
    }
  }
  return Array.from(porCompetencia.values()).sort((a, b) =>
    a.competencia.localeCompare(b.competencia),
  );
}

/** Barras do gráfico "Valor total por DRE": ordem alfabética do código, como no protótipo. */
export function ordenarPorCodigo(consolidado: readonly ConsolidadoPorDre[]): ConsolidadoPorDre[] {
  return [...consolidado].sort((a, b) => a.dreCodigo.localeCompare(b.dreCodigo, 'pt-BR'));
}

/** As competências presentes no recorte inteiro, para alimentar o filtro de período. */
export function competenciasDisponiveis(linhas: readonly LinhaRelatorioSme[]): string[] {
  return Array.from(new Set(linhas.map((l) => l.competencia))).sort();
}

/** "2026-03" → "mar/26". */
export function formatarCompetenciaCurta(competencia: string): string {
  const [ano = '', mes = ''] = competencia.split('-');
  const data = new Date(Number(ano), Number(mes) - 1, 1);
  if (Number.isNaN(data.getTime())) return competencia;
  const rotulo = new Intl.DateTimeFormat('pt-BR', { month: 'short' }).format(data);
  return `${rotulo.replace('.', '')}/${ano.slice(2)}`;
}

/** "2026-03" → "03/26", o eixo X da série temporal no protótipo. */
export function formatarCompetenciaNoEixo(competencia: string): string {
  const [ano = '', mes = ''] = competencia.split('-');
  return `${mes}/${ano.slice(2)}`;
}

/** Percentual de 0 a 1 em "15,0%". Travessão quando não há base para o cálculo. */
export function formatarPercentual(valor: number | null): string {
  if (valor === null) return '—';
  return `${(valor * 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
}

/** Dias em "31,6 d". */
export function formatarDias(valor: number | null): string {
  if (valor === null) return '—';
  return `${valor.toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} d`;
}

/** Inteiro com separador de milhar. */
export function formatarInteiro(valor: number): string {
  return valor.toLocaleString('pt-BR');
}

/** Dinheiro curto para o eixo do gráfico: 450000 centavos → "R$ 4,5k". */
export function formatarDinheiroCurtoDeCentavos(centavos: number): string {
  const reais = centavos / 100;
  if (Math.abs(reais) >= 1000) {
    return `R$ ${Math.round(reais / 1000).toLocaleString('pt-BR')}k`;
  }
  return `R$ ${Math.round(reais).toLocaleString('pt-BR')}`;
}
